import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import Moment from "moment";
import AuthContext from "../../../auth/AuthContext";
import ReportService from "../../../services/ReportService";
import AlertService from "../../../services/AlertService";
import AssessmentService from "../../../services/AssessmentService";
import { Skeleton } from "primereact/skeleton";
import { Dropdown } from "primereact/dropdown";

const AssessmentPracticalReport = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assessmentId, setAssessmentId] = useState(0);
  const [assessments, setAssessments] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const headers = [
    { label: "Display Name", key: "userDisplayName" },
    { label: "Email", key: "userEmail" },
    { label: "Assessment Name", key: "assessmentName" },
    { label: "Date", key: "attendedDate" },
    { label: "Score", key: "practicalScore" },
    { label: "Total Score", key: "totalPracticalScore" },
  ];

  const supportAccess = authContext.getPermissions("report");

  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
  }, [supportAccess, navigate, authContext]);

const loadAssessments = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await AssessmentService.GetPracticalList(authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const options = [{ value: 0, label: "All" }, ...data.data];
        setAssessments(options);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

const loadResults = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await ReportService.getAssessmentPracticalReport(
        assessmentId,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setResults(data.data);
        setFilteredResults(data.data);
        setCurrentPage(0);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [assessmentId, authContext]);


useEffect(() => {
    const controller = new AbortController();
    loadAssessments(controller.signal);
    return () => controller.abort(); 
  }, [loadAssessments]);

useEffect(() => {
    const controller = new AbortController(); 
    loadResults(controller.signal);
    return () => controller.abort(); 
  }, [loadResults, assessmentId]);

  const handleAssessmentFilter = (value) => {
    setAssessmentId(value);
    setCurrentPage(0);
    if (value === 0) {
      setFilteredResults(results);
    } else {
      const filtered = results.filter((item) => item.assessmentId === value);
      setFilteredResults(filtered);
    }
  };

  const paginatedClients = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredResults.slice(startIndex, endIndex);
  }, [filteredResults, currentPage, rowsPerPage]);

  const generatePageNumbers = () => {
    const totalPages = Math.ceil(filteredResults.length / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");

      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);

      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages)
        start = totalPages - 3;

      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) pages.push(i);
      }

      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  // SKELETON
  const skeletonData = Array.from({ length: rowsPerPage }, (_, i) => ({
    id: `skeleton-${i}`,
  }));
  const skeletonBody = () => <Skeleton width="90%" height="1.5rem" />;

  return (
    <div className="container-fluid custom-container position-relative mb-5">
      <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center mb-3">
        <div className="d-flex flex-wrap gap-4 pt-3">
<div className="flex-grow-1"   style={{ minWidth: "220px", maxWidth: "300px" }}>
  <Dropdown
    value={assessmentId}
    onChange={(e) => handleAssessmentFilter(Number(e.value))}
    options={assessments}
    optionLabel="label"
    optionValue="value"
    placeholder="Select an Assessment"
    filter
    filterPlaceholder="Search assessment..."
    disabled={loading}
    className="custom-input-color report-dropdown"
    panelClassName="report-dropdown-panel"
    emptyMessage="No assessments found"
    emptyFilterMessage="No matching assessment found"
    style={{ maxWidth: "300px" }}
  />
</div>
          <div>
            <CSVLink
              data={results}
              headers={headers}
              filename={"Assessment-Practical-Results.csv"}
              className="btn btn-primary"
            >
              <i className="pi pi-upload me-2"></i>Export
            </CSVLink>
          </div>
        </div>
      </div>

      <DataTable
        value={loading ? skeletonData : paginatedClients}
        selection={selectedClients}
        onSelectionChange={(e) => setSelectedClients(e.value)}
        selectionMode="checkbox"
        emptyMessage={loading ? "" : "No Data Found"}
        dataKey="id"
        responsiveLayout="scroll"
        className="custom-table"
         pt={{
    root: { style: { cursor: "default" } },
    bodyRow: { style: { cursor: "default" } },
    checkbox: { style: { cursor: "default" } }
  }}
      >
        <Column
          header="S. No."
          body={
            loading
              ? skeletonBody
              : (_rowData, { rowIndex }) =>
                  currentPage * rowsPerPage + rowIndex + 1
          }
          style={{ width: "80px", textAlign: "center" }}
          bodyClassName="text-center"
          headerClassName="text-center"
        />
        <Column
          field="userDisplayName"
          header="Display Name"
          sortable
          body={loading ? skeletonBody : (rowData) => rowData.userDisplayName}
        />
        <Column
          field="userEmail"
          header="Email"
          sortable
          body={loading ? skeletonBody : (rowData) => rowData.userEmail}
        />
        <Column
          field="assessmentName"
          header="Assessment Name"
          sortable
          body={loading ? skeletonBody : (rowData) => rowData.assessmentName}
        />
        <Column
          field="attendedDate"
          header="Date"
          body={
            loading
              ? skeletonBody
              : (rowData) =>
                  rowData.attendedDate
                    ? Moment(rowData.attendedDate + "Z").format(
                        "DD/MM/YYYY hh:mm A"
                      )
                    : "-"
          }
        />
        <Column
          field="practicalScore"
          header="Score"
          bodyClassName="text-center"
          body={loading ? skeletonBody : (rowData) => rowData.practicalScore}
        />
        <Column
          field="totalPracticalScore"
          header="Total Score"
          bodyClassName="text-center"
          body={
            loading ? skeletonBody : (rowData) => rowData.totalPracticalScore
          }
        />
      </DataTable>

      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
        <div className="d-flex align-items-center">
          <label className="me-2 fw-bold">Show Rows:</label>
          {loading ? (
            <Skeleton width="100px" height="38px" />
          ) : (
            <select
              className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
            >
              {[10, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="d-flex gap-2">
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
            <Skeleton width="40px" height="40px" />
          </div>
        ) : (
          <div className="d-flex justify-content-center align-items-center gap-2">
            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >
              &lt;
            </button>
            {generatePageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={index} className="mx-1 text-secondary">
                  ...
                </span>
              ) : (
                <button
                  key={index}
                  className={`btn p-0 pagination-item ${
                    currentPage + 1 === page ? "active-page" : ""
                  }`}
                  style={{ width: "40px", height: "40px" }}
                  onClick={() => setCurrentPage(page - 1)}
                >
                  {page}
                </button>
              )
            )}
            <button
              className="btn p-0 fs-5 pagination-item"
              style={{ width: "40px", height: "40px" }}
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    Math.ceil(filteredResults.length / rowsPerPage) - 1,
                    prev + 1
                  )
                )
              }
              disabled={
                currentPage >=
                Math.ceil(filteredResults.length / rowsPerPage) - 1
              }
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentPracticalReport;
